import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, ArrowLeft, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';
import EmptyState from '@/components/premium/EmptyState';
import { PublishingIllustration } from '@/components/premium/illustrations';
import { SkeletonList } from '@/components/premium/Skeleton';

const STATUS_META = {
  draft:     { color: '#71717a', label: 'Draft' },
  scheduled: { color: '#00E5FF', label: 'Scheduled' },
  published: { color: '#22C55E', label: 'Published' },
  failed:    { color: '#EF4444', label: 'Failed' },
};

const PLATFORM_ACCENT = {
  tiktok: '#FF3D7F', instagram: '#E1306C', youtube: '#FF0000', linkedin: '#0A66C2',
};
const PLATFORM_LABEL = {
  tiktok: 'TikTok', instagram: 'Instagram Reels', youtube: 'YouTube Shorts', linkedin: 'LinkedIn',
};

const fmtDate = (iso) => (iso ? iso.slice(0, 16).replace('T', ' ') : '—');

const PublishingHistory = () => {
  const { lang } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.get('/social/publications?limit=50')
      .then((r) => setRows(r.data.publications || []))
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto px-6 lg:px-10 py-10"
      data-testid="history-page"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/dashboard" className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono flex items-center gap-2 hover:text-zinc-200" data-testid="history-back-link">
            <ArrowLeft className="w-3 h-3" /> Dashboard
          </Link>
          <h1 className="font-heading text-4xl lg:text-5xl font-black mt-2 tracking-tighter">
            {lang === 'fr' ? 'Historique de publication' : 'Publishing History'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1 font-mono">
            {lang === 'fr' ? 'Suivi de toutes les publications programmées, publiées et échouées' : 'All scheduled, published and failed publications'}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="history-refresh-btn"
          className="px-4 py-2 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] disabled:opacity-50 text-xs uppercase tracking-wider font-mono flex items-center gap-2"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/5 text-red-300 text-sm mb-6 font-mono" data-testid="history-error">
          Error: {error}
        </div>
      )}

      {loading && !rows.length ? (
        <div data-testid="history-loading"><SkeletonList rows={4} /></div>
      ) : !rows.length ? (
        <EmptyState
          testid="history-empty"
          illustration={<PublishingIllustration className="w-56 h-40" />}
          title={lang === 'fr' ? 'Aucune publication pour le moment' : 'No publications yet'}
          description={lang === 'fr' ? 'Utilise le bouton « Publier » sur n\'importe quel clip pour poster sur TikTok, YouTube Shorts, Instagram Reels ou LinkedIn.' : 'Use the "Publish" button on any clip to post to TikTok, YouTube Shorts, Instagram Reels or LinkedIn.'}
          ctaLabel={lang === 'fr' ? 'Voir mes clips' : 'View my clips'}
          to="/dashboard"
        />
      ) : (
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden">
        <table className="w-full text-sm" data-testid="history-table">
          <thead className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5 font-mono">
            <tr>
              <th className="p-3">When</th>
              <th className="p-3">Platform</th>
              <th className="p-3">Clip</th>
              <th className="p-3">Status</th>
              <th className="p-3">Link</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const stmeta = STATUS_META[r.status] || STATUS_META.draft;
              return (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`history-row-${r.id}`}>
                  <td className="p-3 font-mono text-xs text-zinc-500">{fmtDate(r.created_at)}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PLATFORM_ACCENT[r.platform] || '#888' }} />
                      <span className="text-sm text-zinc-100">{PLATFORM_LABEL[r.platform] || r.platform}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <Link to={`/clips/${r.clip_id}`} className="text-sm text-zinc-100 hover:text-[#D4FF00]" data-testid={`history-clip-link-${r.id}`}>
                      {r.clip_snippet?.title || r.clip_id.slice(0, 8)}
                    </Link>
                    {r.video_snippet?.title && (
                      <div className="text-[11px] text-zinc-500 font-mono truncate max-w-[280px]">{r.video_snippet.title}</div>
                    )}
                  </td>
                  <td className="p-3">
                    <span
                      className="text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded border"
                      style={{ color: stmeta.color, borderColor: `${stmeta.color}66` }}
                      data-testid={`history-status-${r.id}`}
                    >
                      {stmeta.label}
                    </span>
                    {r.error && (
                      <div className="text-[11px] text-red-400/80 mt-1 max-w-xs" data-testid={`history-error-${r.id}`}>
                        {r.error.length > 90 ? r.error.slice(0, 90) + '…' : r.error}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    {r.external_url ? (
                      <a href={r.external_url} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-[#D4FF00] inline-flex items-center gap-1 text-xs font-mono" data-testid={`history-external-${r.id}`}>
                        <ExternalLink className="w-3 h-3" /> {lang === 'fr' ? 'Ouvrir' : 'Open'}
                      </a>
                    ) : (
                      <span className="text-zinc-600 text-xs font-mono">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </motion.div>
  );
};

export default PublishingHistory;
